import Errors from '@/shared/error/errors';
import { storeAsync } from "@/app-module";
import RealEstateRegisterService from '@/shared/services/real-estate-register.service';
import { AncpiService } from "@/shared/services/ancpi.service";

// Helper function to generate userKey from user_id
function generateUserKey(userId) {
    if (!userId) return null;
    // Convert user_id to string and encode it with base64
    const userString = String(userId);
    const encoded = btoa(userString); // Base64 encode
    // Create a hash-like key for better obfuscation
    let hash = 0;
    for (let i = 0; i < encoded.length; i++) {
        const char = encoded.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return `${Math.abs(hash)}`;
}

// Action types
export const INIT = 'init';
export const LOAD_PAGE_DATA = 'loadPageData';
export const ADD_REAL_ESTATE_ITEM = 'addRealEstateItem';
export const REMOVE_REAL_ESTATE_ITEM = 'removeRealEstateItem';
export const RESET_PAGE_STATE = 'resetPageState';
export const LOAD_FROM_LOCAL_STORAGE = 'loadFromLocalStorage';
export const SYNC_TO_LOCAL_STORAGE = 'syncToLocalStorage';
export const DO_SEARCH_PARCEL = "searchParcel";

// Mutation types
export const SET_PAGE_DATA = 'setPageData';
export const SET_REAL_ESTATE_LIST = 'setRealEstateList';
export const SET_TOTAL_COUNT = 'setTotalCount';
export const ADD_ITEM = 'addItem';
export const REMOVE_ITEM = 'removeItem';
export const LOAD_CACHED_DATA = 'loadCachedData';
export const SAVE_TO_CACHE = 'saveToCache';
export const SET_USER_KEY = 'setUserKey';

// Initialize state with default values - will be populated by INIT action
const cachedRealEstateList = RealEstateRegisterService.getRealEstateList(null);

const state = {
    pageData: {
        lastUpdated: null,
        counter: 0
    },
    realEstateList: cachedRealEstateList,
    totalCount: cachedRealEstateList.length,
    userKey: null,
};

const getters = {
    error: (state) => state.error,
    pageData: (state) => state.pageData,
    realEstateList: (state) => state.realEstateList,
    totalCount: (state) => state.totalCount,
    hasData: (state) => state.realEstateList.length > 0,
    counter: (state) => state.pageData.counter,
    userKey: (state) => state.userKey
};

const mutations = {
    [SET_PAGE_DATA](state, data) {
        state.pageData = { ...state.pageData, ...data };
    },
    [SET_REAL_ESTATE_LIST](state, data) {
        state.realEstateList = data;
        // Auto-sync to localStorage when list changes
        RealEstateRegisterService.saveRealEstateList(data, state.userKey);
    },
    [SET_TOTAL_COUNT](state, count) {
        state.totalCount = count;
    },
    [ADD_ITEM](state, itemData) {
        const { data, isEdit, index } = itemData;
        if (isEdit && data.id) {
            const idx = state.realEstateList.findIndex(item => item.id === data.id);
            if (idx !== -1) {
                state.realEstateList.splice(idx, 1, data);
            }
        } else {
            state.realEstateList.push(data);
            state.totalCount++;
        }
        // Auto-sync to localStorage when property is added or updated
        RealEstateRegisterService.saveRealEstateList(state.realEstateList, state.userKey);
    },
    [REMOVE_ITEM](state, itemId) {
        const index = state.realEstateList.findIndex(item => item.id === itemId);
        if (index >= 0) {
            state.realEstateList.splice(index, 1);
            state.totalCount--;
            // Auto-sync to localStorage when item is removed
            RealEstateRegisterService.saveRealEstateList(state.realEstateList, state.userKey);
        }
    },
    [LOAD_CACHED_DATA](state) {
        const cachedList = RealEstateRegisterService.getRealEstateList(state.userKey);
        if (cachedList.length > 0) {
            state.realEstateList = [...cachedList];
            state.totalCount = cachedList.length;
        }
    },
    [SAVE_TO_CACHE](state) {
        RealEstateRegisterService.saveRealEstateList(state.realEstateList, state.userKey);
    },
    [SET_USER_KEY](state, userKey) {
        state.userKey = userKey;
    },
};

const actions = {
    async [INIT](context) {
        try {
            const store = storeAsync();
            const currentUser = await store.getters['auth/currentUser'];
            const userId = currentUser?.user_id;
            const userKey = generateUserKey(userId);

            // Set the user key
            context.commit(SET_USER_KEY, userKey);

            // Load cached data with the user key
            const cachedList = RealEstateRegisterService.getRealEstateList(userKey);
            if (cachedList.length > 0) {
                context.commit(SET_REAL_ESTATE_LIST, cachedList);
                context.commit(SET_TOTAL_COUNT, cachedList.length);
            }
        } catch (error) {
            // Fallback - load without user key
            context.commit(LOAD_CACHED_DATA);
        }
    },

    async [LOAD_PAGE_DATA](context) {
        context.commit(LOAD_CACHED_DATA);
    },

    async [ADD_REAL_ESTATE_ITEM](context, {data, isEdit, index}) {
        context.commit(ADD_ITEM, {data, isEdit, index});
    },

    async [REMOVE_REAL_ESTATE_ITEM](context, itemId) {
        context.commit(REMOVE_ITEM, itemId);
    },

    [RESET_PAGE_STATE](context) {
        context.commit(SET_PAGE_DATA, {
            lastUpdated: null,
            counter: 0
        });
        context.commit(SET_REAL_ESTATE_LIST, []);
        context.commit(SET_TOTAL_COUNT, 0);
        // Clear localStorage when resetting
        RealEstateRegisterService.clearRealEstateList(context.state.userKey);
    },

    [LOAD_FROM_LOCAL_STORAGE](context) {
        try {
            context.commit(LOAD_CACHED_DATA);
        } catch (error) {
            // If localStorage is corrupted, clear it
            RealEstateRegisterService.clearRealEstateList(context.state.userKey);
        }
    },

    [SYNC_TO_LOCAL_STORAGE](context) {
        context.commit(SAVE_TO_CACHE);
    },

    async [DO_SEARCH_PARCEL](context, payload) {
        context.commit("shared/activateLoading", "realEstateRegister/searchParcel", {
            root: true,
        });
        try {
            const parcel = await AncpiService.searchParcel(payload);
            return parcel;
        } catch (error) {
            Errors.handle(error);
        } finally {
            context.commit("shared/deactivateLoading", "realEstateRegister/searchParcel", {
                root: true,
            });
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};